import { Field, FieldRenderProps, Form } from "react-final-form";
import Button from "ui-kit/src/ButtonComponent/Button";
import { BaseModal } from "./BaseModal";
import { useModal } from "../../features/modalProvider";
import { _ } from "i18n";
import { Keys } from "i18n/locales/types";
import { useState, useEffect, useRef } from "react";
import { SelectOption } from "hooks/apiManagement/useVideoSourceOptions";
import { usePlayerSearch } from "../../hooks/apiManagement/usePlayerSearch";
import Select from "react-select";
import type { SingleValue } from "react-select";
import '../../style/mapModal.css'
import { useDebounce } from "../../hooks/apiManagement/useDebounce";

interface MapModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (value: string) => void;
    isLoading?: boolean;
}

interface FormValues {
    mapValue: string;
}

export const MapModal: React.FC<MapModalProps> = ({ isOpen, onClose, onSubmit, isLoading = false }) => {
    const { showFeedback } = useModal();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOption, setSelectedOption] = useState<SingleValue<SelectOption>>(null);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [isOptionSelected, setIsOptionSelected] = useState(false);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const { searchResults, isLoading: isSearchLoading, hasError, clearResults } = usePlayerSearch(isOptionSelected ? "" : debouncedSearchTerm);

    const handleSubmit = (values: FormValues) => {
        if (selectedOption && selectedOption.value) {
            onSubmit(String(selectedOption.value));
            return;
        }
        
        if (values.mapValue && /^\d+$/.test(values.mapValue.trim())) {
            onSubmit(values.mapValue.trim());
            return;
        }
        
        if (searchTerm && /^\d+$/.test(searchTerm.trim())) {
            onSubmit(searchTerm.trim());
            return;
        }

        showFeedback(
            _("video-tasks.modal.teamsheet.map.warning" as keyof Keys),
            'warning',
        );
    };

    const resetSearch = () => {
        setSearchTerm("");
        setSelectedOption(null);
        setMenuIsOpen(false);
        setIsOptionSelected(false);
        clearResults();
    };

    useEffect(() => {
        if (!isOpen) {
            resetSearch();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isLoading) {
            onClose();
        }
    }, [isLoading]);

    const renderField = ({ input, meta }: FieldRenderProps<string, HTMLInputElement, string>) => {
        const handleSelectChange = (option: SingleValue<SelectOption>) => {
            setSelectedOption(option);
            setMenuIsOpen(false);
            if (option) {
                input.onChange(String(option.value));
                setSearchTerm(option.label);
                setIsOptionSelected(true);
                clearResults();
            } else {
                input.onChange('');
                setSearchTerm('');
                setIsOptionSelected(false);
            }
        };

        const handleInputChange = (value: string) => {
            setSearchTerm(value);
            
            if (!isOptionSelected) {
                input.onChange(value);
            }
            
            if (value && !menuIsOpen) {
                setMenuIsOpen(true);
            }

            if (selectedOption && value.length > 0 && value !== selectedOption.label) {
                setSelectedOption(null);
                setIsOptionSelected(false);
                input.onChange(value);
            }

            if (isOptionSelected && value.length > 0 && value !== selectedOption?.label) {
                setIsOptionSelected(false);
                input.onChange(value);
            }
        };

        const handleMenuOpen = () => {
            setMenuIsOpen(true);
        };

        const handleMenuClose = () => {
            setMenuIsOpen(false);
        };

        return (
            <div className="search-container">
                <Select
                    className={`react-select ${meta.touched && meta.error ? 'has-error' : ''}`}
                    classNamePrefix="react-select"
                    placeholder={_("video-tasks.modal.teamsheet.map.search_by_name_or_id" as keyof Keys)}
                    options={searchResults}
                    value={selectedOption}
                    onChange={handleSelectChange}
                    onInputChange={handleInputChange}
                    inputValue={searchTerm}
                    isLoading={isSearchLoading}
                    isClearable
                    isSearchable
                    closeMenuOnSelect={true}
                    blurInputOnSelect={false}
                    menuIsOpen={menuIsOpen}
                    onMenuOpen={handleMenuOpen}
                    onMenuClose={handleMenuClose}
                    noOptionsMessage={({ inputValue }) => 
                        inputValue ? _("datatables.zero_records") : _("label.search")
                    }
                    autoFocus
                />
                {meta.touched && meta.error && (
                    <div className="error-message">{meta.error}</div>
                )}
            </div>
        );
    };

    const renderForm = ({ handleSubmit }: { handleSubmit: () => void }) => {
        const isButtonDisabled = isLoading || hasError || (!selectedOption && (!searchTerm || !/^\d+$/.test(searchTerm.trim()))) || (!selectedOption && searchResults.length === 0);

        return (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <div className="form-row" style={{ width: '100%' }}>
                    <div className="field-group" style={{ width: '100%', display: "flex", flexDirection: "column", gap: "20px"}}>
                        <Field name="mapValue" type="text" component={renderField} />
                        <Button 
                            variant="primary"
                            className="buttonSubmit"
                            type="submit"
                            block={true}
                            disabled={isButtonDisabled}
                            loading={isLoading}
                        >
                            {isLoading ? "Mapping..." : _("video-tasks.modal.teamsheet.map.submit" as keyof Keys)}
                        </Button>
                    </div>
                </div>
            </form>
        );
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={_("video-tasks.modal.teamsheet.map.title" as keyof Keys)}
            size="lg"
            className="video-tasks-map-modal"
        >
            <Form
                onSubmit={handleSubmit}
                render={renderForm}
            />
        </BaseModal>
    );
};